import _ from "lodash";
import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { compose, graphql } from "lib/apolloClient";
import { useI18n } from "Hooks";

import { setToastMsg } from "Login/modules/LoginModule/LoginActions";

import { Modal, Button, ToggleSwitch } from "@toddle-edu/ds-web";

import { FormTextAreaInput } from "UIComponents/TextAreaInput";
import { FormTextInput } from "UIComponents/InputTextField";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import withLoader from "UIComponents/GqlWithLoader/GqlWithLoader";

import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";

import {
  ORGANIZATION,
  TIMETABLE_SYNC,
  TIMETABLE_SYNC_SUB_TYPES,
} from "AddOnManager/routes/OrgAddOn/modules/OrgAddOnContants.js";
import { updateAddOnStatus } from "AddOnManager/routes/OrgAddOn/modules/OrgAddOnHelpers";
import { getInternalToolGenericDataQuery } from "InternalTools/modules/InternalToolsQuery";
import { getInternalToolGenericDataVariables } from "AddOnManager/modules/AddOnManagerUtils";

import classes from "./TimetableSync.scss";

const TimetableSyncWrapper = props => {
  const {
    orgId,
    updateAddOnStatus,
    setToastMsg,
    internalToolGenericDataVariables,
    configValues,
    onSuccessAddOnConfigChange,
  } = props;
  const { t } = useI18n(["integration", "common"]);

  // assign default value to the state
  // rendered at first refresh // TO check with timetable query
  const [orgLevelToggleMap, setOrgLevelToggleMap] = useState({
    [TIMETABLE_SYNC_SUB_TYPES.BASE_SYNC]: _.get(
      configValues,
      `syncModule`,
      false
    ),
    [TIMETABLE_SYNC_SUB_TYPES.LR_ARCH_ENABLE]: _.get(
      configValues,
      `newIntegrationLRArchitecture`,
      false
    ),
  });

  const [fullScreenLoader, setFullScreenLoader] = useState(false);
  const [jsonPayload, setJsonPayload] = useState("");
  const [jsonPayloadError, setJsonPayloadError] = useState("");

  const handleOrgLevelToggle = async ({ addOnSubType }) => {
    const previousStatus = orgLevelToggleMap[addOnSubType];
    const newStatus = !previousStatus;
    setFullScreenLoader(true);
    try {
      let additionalInput = {};
      if (_.trim(jsonPayload)) {
        try {
          // allow only object, {key:value} pair,
          // reject all other format
          additionalInput = JSON.parse(jsonPayload);
          const isValidObject =
            additionalInput &&
            typeof additionalInput === "object" &&
            !Array.isArray(additionalInput) &&
            !_.isEmpty(additionalInput);
          if (!isValidObject) {
            throw new Error("Invalid JSON structure");
          }
          setJsonPayloadError("");
        } catch (e) {
          setJsonPayloadError(t("integration:invalid_json_format"));
          return;
        }
      }

      setOrgLevelToggleMap(prev => ({
        ...prev,
        [addOnSubType]: newStatus,
      }));
      await updateAddOnStatus({
        orgId,
        addOnType: TIMETABLE_SYNC,
        updateOn: ORGANIZATION,
        newStatus,
        queryVariables: internalToolGenericDataVariables,
        addOnSubType,
        additionalInput,
      });
      if (!_.isNil(onSuccessAddOnConfigChange)) onSuccessAddOnConfigChange();
    } catch (err) {
      // catch: revert UI back to previous status, show error incase of API failure
      setOrgLevelToggleMap(prev => ({
        ...prev,
        [addOnSubType]: previousStatus,
      }));
      setToastMsg("toastMsgs:something_went_wrong");
    } finally {
      setFullScreenLoader(false);
    }
  };

  const handleChangeInput = useCallback(
    value => {
      setJsonPayload(value);
      if (jsonPayloadError) setJsonPayloadError("");
    },
    [jsonPayloadError]
  );

  return (
    <div className={classes.container}>
      <FormTextInput
        size="large"
        label={t("common:org_id")}
        error=""
        value={orgId}
        isRequired={true}
        disabled={true}
      />

      <div className={classes.userInputSection}>
        {t("integration:config_label", {
          label: t("integration:timetable_sync"),
        })}

        <FormTextAreaInput
          minRows={4}
          value={jsonPayload}
          placeholder={t("integration:config_details_placeholder")}
          updateInputField={handleChangeInput}
          testId="timetable-sync-config-textarea"
        />
        {jsonPayloadError ? (
          <div className={classes.jsonError}>{jsonPayloadError}</div>
        ) : null}
      </div>

      <div className={classes.switchContainer}>
        <div>{t("integration:timetable_sync")}</div>
        <div className={classes.actionItems}>
          <ToggleSwitch
            checked={orgLevelToggleMap[TIMETABLE_SYNC_SUB_TYPES.BASE_SYNC]}
            onChange={() =>
              handleOrgLevelToggle({
                addOnSubType: TIMETABLE_SYNC_SUB_TYPES.BASE_SYNC,
              })
            }
          />
        </div>
      </div>

      <div className={classes.switchContainer}>
        <div>{t("integration:lr_architecture_enable")}</div>
        <div className={classes.actionItems}>
          <ToggleSwitch
            checked={orgLevelToggleMap[TIMETABLE_SYNC_SUB_TYPES.LR_ARCH_ENABLE]}
            onChange={() =>
              handleOrgLevelToggle({
                addOnSubType: TIMETABLE_SYNC_SUB_TYPES.LR_ARCH_ENABLE,
              })
            }
          />
        </div>
      </div>

      {fullScreenLoader ? <FullScreenLoader /> : null}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { orgId } = ownProps;

  const internalToolGenericDataVariables = getInternalToolGenericDataVariables({
    orgId,
    addOnType: TIMETABLE_SYNC,
  });

  return {
    isData: true,
    isLoading: false,
    internalToolGenericDataVariables,
    orgId,
  };
};

const mapActionCreators = { updateAddOnStatus, setToastMsg };

const TimetableSyncWithLoader = compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getInternalToolGenericDataQuery, {
    name: "getInternalToolGenericData",
    skip: ({ orgId }) => _.isEmpty(orgId),
    options: ({ internalToolGenericDataVariables }) => ({
      variables: internalToolGenericDataVariables,
      fetchPolicy: "cache-and-network",
    }),
    props: ({
      getInternalToolGenericData,
      ownProps: { isData, isLoading },
    }) => {
      const data = _.get(getInternalToolGenericData, "platform");

      const queryData = _.get(data, "internalToolsGenericQuery.data", []);

      return {
        configValues: queryData,
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes(
            GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
            getInternalToolGenericData.networkStatus
          ) || isLoading,
      };
    },
  }),
  withLoader
)(TimetableSyncWrapper);

const TimetableSync = ({
  orgId,
  closeModal,
  onSuccessAddOnConfigChange = null,
}) => {
  const { t } = useI18n(["integration", "common"]);

  // why fixed height and width?
  return (
    <Modal width="580px" height="max-content" isOpen onClose={closeModal}>
      <Modal.Header title={t("integration:timetable_sync")} isClosable />
      <Modal.Body isScrollable>
        <TimetableSyncWithLoader
          closeModal={closeModal}
          orgId={orgId}
          onSuccessAddOnConfigChange={onSuccessAddOnConfigChange}
        />
      </Modal.Body>

      <Modal.Footer>
        <div className={classes.footer}>
          <div className={classes.rightFooterContainer}>
            <Button
              _legacy_variant="outlined-subtle"
              variant="neutral"
              type="outlined"
              size="large"
              onClick={closeModal}
            >
              {t("common:close")}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default TimetableSync;
